/** Where the last hand-off landed in a conversation's composer. */
export type PreviousHandOff = {
  text: string;
  start: number;
};

/**
 * - `placed`: set the composer to `text` now.
 * - `confirm_replace`: the earlier hand-off was edited, so ask before setting
 *   the composer to `text`, and leave it alone if the expert declines.
 *
 * `start` is where the composed text begins, to remember for the next hand-off.
 */
export type HandOffPlacement =
  | { kind: 'placed'; text: string; start: number }
  | { kind: 'confirm_replace'; text: string; start: number };

const PARAGRAPH = '\n\n';

function joinAfter(inserted: string, rest: string): string {
  if (!rest) {
    return inserted;
  }

  return /^\s/.test(rest) ? inserted + rest : `${inserted} ${rest}`;
}

/**
 * Decides the composer text after "use draft", so the draft expands where
 * `.draft` was typed, the way a citation expands where `.mypubs` was.
 *
 * The first hand-off goes in at the token, keeping the text before it and
 * anything after it. If the composer changed so the token's place is past its
 * end, the text is appended as a new paragraph instead.
 *
 * A later hand-off replaces the earlier one when the composer still holds it
 * unchanged. Once the expert has edited it, their edits are theirs: the text
 * before the earlier hand-off is kept, the rest would be replaced, and the
 * caller asks first.
 */
export function insertHandOff({
  composerText,
  tokenStart,
  composedText,
  previous,
}: {
  composerText: string;
  tokenStart: number | null;
  composedText: string;
  previous: PreviousHandOff | null;
}): HandOffPlacement {
  if (previous) {
    const previousStart = composerText.indexOf(previous.text);

    if (previousStart >= 0) {
      return {
        kind: 'placed',
        text:
          composerText.slice(0, previousStart) +
          composedText +
          composerText.slice(previousStart + previous.text.length),
        start: previousStart,
      };
    }

    const keptUntil = Math.min(previous.start, composerText.length);

    return {
      kind: 'confirm_replace',
      text: composerText.slice(0, keptUntil) + composedText,
      start: keptUntil,
    };
  }

  if (tokenStart !== null && tokenStart <= composerText.length) {
    const before = composerText.slice(0, tokenStart);

    return {
      kind: 'placed',
      text: before + joinAfter(composedText, composerText.slice(tokenStart)),
      start: tokenStart,
    };
  }

  if (!composerText.trim()) {
    return { kind: 'placed', text: composedText, start: 0 };
  }

  const separator = /\s$/.test(composerText) ? '' : PARAGRAPH;

  return {
    kind: 'placed',
    text: composerText + separator + composedText,
    start: composerText.length + separator.length,
  };
}
